/**
 * The wire shapes a Courier's device speaks — ADR-0034, and D2's protocol.
 *
 * Two directions and they are deliberately asymmetric (ADR-0009): **events up, snapshots
 * down.** The device appends immutable facts and never edits a shared record; the server
 * answers with a projection that has a redaction schedule rather than a replica to merge.
 * That asymmetry is what lets §4's visibility windows expire on a device the server cannot
 * reach.
 */
import { z } from 'zod';
import { renderedText } from './rendered-text.js';
/**
 * Ops mints an enrolment for one Courier. The body is empty, and deliberately so.
 *
 * A per-handset label was drafted and cut: `device_enrolments` has nowhere to put one, one
 * binding serves every handset a Courier enrols, and the field would have been accepted,
 * validated and read by nothing. That is the exact shape of the two defects this repository
 * has already paid for — `codFeeMinor` wrong for a milestone because nothing read it, and
 * `rto_fee_terms` carrying a currency that disagreed with the rate cards. An empty object
 * keeps the route shaped for a body it may later need without pretending it has one.
 */
export const enrolDeviceSchema = z.object({});
export const enrolDeviceRendered = renderedText(enrolDeviceSchema, {});
/**
 * Why a binding is being revoked — a **closed** enum, on the pattern §5's pause reasons set.
 *
 * Free text would be the easy choice and the wrong one twice over. It would put an operator's
 * prose into an append-only store nothing can correct (I3) — the same argument that keeps the
 * bootstrap email out of `principal.bound` — and it would make "how many handsets were lost
 * this quarter" unanswerable without reading every row. The values also have to satisfy
 * migration 0001's `events_reason_code_vocabulary` CHECK, which a free string would not.
 *
 * `device_lost` and `device_stolen` are kept apart deliberately: they call for different
 * follow-up, and merging them would lose the distinction at the moment it is cheapest to
 * record.
 */
export const REVOCATION_REASONS = [
    'device_lost',
    'device_stolen',
    'staff_departed',
    'compromise_suspected',
    'enrolment_error',
];
export const revokePrincipalSchema = z.object({
    reasonCode: z.enum(REVOCATION_REASONS),
});
export const revokePrincipalRendered = renderedText(revokePrincipalSchema, {});
/**
 * The device redeems its one-time code.
 *
 * **It does not choose its own device id** — the server mints one and returns it. A client
 * that picked its own could reinstall, reuse the id, restart its monotonic counter at 1 and
 * collide with its own history under `events_device_sequence_unique`, which is the
 * idempotency key the whole sync protocol rests on (migration 0010).
 */
export const redeemEnrolmentSchema = z.object({
    code: z.string().trim().min(8).max(128),
});
export const redeemEnrolmentRendered = renderedText(redeemEnrolmentSchema, {
    exempt: { code: 'a one-time enrolment code, only hashed and matched against a stored hash; never stored or shown' },
});
/**
 * Machine 1's triggers a **Courier** may cause, as opposed to the ones a dispatcher causes.
 *
 * `SUPPORTED_TRIGGERS` in `shipments.ts` is one flat union covering both, because until now
 * there was only one caller and it was always Ops. It is not sliceable by inspection: the
 * 02a actor column is the authority on who may cause what, and it disagrees with intuition
 * often enough to matter — `rto.close` posts a fee and is the platform's act, while a door
 * refusal is the *Buyer's*.
 *
 * So this list is checked against the machine rather than trusted: a test asserts every
 * member is in `SUPPORTED_TRIGGERS` **and** that its transition row names `courier` among
 * its actors. A dispatcher act reachable from a handset would be an authority bug that no
 * amount of reading catches.
 *
 * ## What is missing from this list, and why it is not an oversight
 *
 * **A door refusal is not here — it is in `COURIER_DELEGATED_TRIGGERS` below.** 02a's actor
 * column for `rto.initiated` reads `opsUser, system, buyer, merchantUser` and does not name
 * the Courier, correctly: refusing the parcel is the *Buyer's* act and the Courier
 * witnesses it. It reaches the server through delegation rather than by joining this list,
 * because this list means "a Courier caused this" and that would be the wrong fact in the
 * one place a refusal dispute is decided.
 */
export const COURIER_TRIGGERS = [
    'leg.pickup.start',
    'leg.pickup.arrive',
    'leg.pickup.confirm',
    'leg.pickup.fail',
    'leg.delivery.start',
    'leg.delivery.arrive',
    'leg.delivery.confirm',
    'leg.delivery.attempt_fail',
    // Actors are `system, courier`, and 02a evaluates `n < N` **device-locally** so the
    // re-attempt cycle keeps working through an outage. This is the one trigger a handset
    // causes on its own initiative rather than at somebody's door.
    'leg.delivery.reattempt',
    'leg.return.start',
    'leg.return.refuse',
    'leg.return.confirm',
];
/**
 * What a Courier may record **on someone else's behalf** — ADR-0035, ruling 1.
 *
 * One entry, and the shape is deliberately narrower than a permission: the trigger, the
 * single role it may be attributed to, and the closed set of reasons that qualify. A
 * Courier cannot choose the delegation — ingest infers it from this table — so a handset
 * cannot claim to be acting for anyone, which is the property `applyTransition`'s check
 * exists to defend.
 *
 * ## Why only these two reasons
 *
 * `RTO_REASONS` has six. Four of them — `buyer_cancel`, `merchant_recall`,
 * `buyer_unreachable`, `address_unresolvable` — are decisions made *away* from the door by
 * Ops, the Merchant, or the platform's own attempt counter. A Courier recording
 * `merchant_recall` would be asserting a Merchant instruction nobody gave. The two that
 * remain are exactly the ones that happen in front of the Courier: the Buyer took none of
 * it, or wanted part of it and the all-or-nothing rule refused the whole (ADR-0014).
 *
 * `partial_refusal` is the one the revisit gate counts, which is why it is a separate
 * reason rather than a note on the first.
 */
export const COURIER_DELEGATED_TRIGGERS = {
    'rto.initiate': {
        onBehalfOf: 'buyer',
        reasons: ['full_refusal', 'partial_refusal'],
    },
};
/** Every trigger a handset may send: caused by the Courier, or witnessed by them. */
export const COURIER_SENDABLE_TRIGGERS = [
    ...COURIER_TRIGGERS,
    ...Object.keys(COURIER_DELEGATED_TRIGGERS),
];
export function delegationFor(trigger) {
    return (COURIER_DELEGATED_TRIGGERS[trigger] ?? null);
}
/**
 * One captured fact from the device.
 *
 * `deviceSequence` is the device's own monotonic counter — "monotonic per device, never
 * reused, gap-free within an install" (the kernel's `DeviceProvenance`). Gap-free is what
 * lets the ack be a single integer rather than a set.
 *
 * `capturedAt` is the device clock and **may be wrong**; that is expected and is not
 * rejected. ADR-0013 rule 3 grants the benefit of offline doubt to the Courier's clock,
 * bounded five minutes forward, and flags rather than corrects anything past it.
 */
export const courierEventSchema = z.object({
    deviceSequence: z.coerce.number().int().nonnegative(),
    capturedAt: z.string().datetime({ offset: true }),
    shipmentId: z.string().trim().min(1),
    trigger: z.enum(COURIER_SENDABLE_TRIGGERS),
    /**
     * Validated against Machine 1's per-trigger payload schema at ingest, not here. Keeping
     * one payload vocabulary in `shipments.ts` is the point: a second copy for the device
     * would be free to drift from the one the Ops console posts, and the two must produce
     * byte-identical events or a dispute reads differently depending which typed it.
     */
    payload: z.record(z.unknown()).default({}),
});
/**
 * The reasons a device deletes a Leg it was holding.
 *
 * The kernel's `WindowClosedReason` minus `sealed_unopened`, and the subtraction is the
 * substance: a live seal is a window that is *closed but still held*, so it is precisely the
 * one reason that never accompanies a deletion. Restated here rather than imported because
 * `@tard/contracts` is the wire vocabulary and must not acquire a dependency on the kernel
 * — but the two are pinned together by a test, so the subtraction cannot silently become a
 * divergence.
 */
export const REDACTION_REASONS = [
    'not_assigned',
    'assigned_to_another_courier',
    'assignment_revoked',
    'grace_window_elapsed',
    'device_ttl_expired',
    'sealed_expired',
];
/**
 * The device reporting that it forgot something — D2's lane, in D2's own words.
 *
 * D2 specifies it outright: *"A local job redacts expired Leg details per the I1 windows
 * even while offline (the expiry rule ships with the snapshot), and **emits a
 * `redaction-completed` event through the same outbox**."* Same outbox and **the same
 * counter**, which is what makes it more than a log line: it is ordered against the
 * Courier's other work, it is idempotent under the one `(device_id, device_sequence)` key,
 * and it survives a crash exactly as a delivery confirmation does.
 *
 * ## Why the platform wants it
 *
 * §4's device redaction is otherwise the one control in this codebase that leaves **no
 * evidence it ran**. Every other invariant here is demonstrable after the fact — the ledger
 * balances, the events derive the projection, the leak gate is a CI signal. A redaction
 * timer that fired on a handset in Tripoli six hours into an outage leaves nothing at all,
 * so "did the Buyer's address actually leave that device?" was unanswerable except by
 * trusting the code. §7 asks us to *"log enough to demonstrate diligence"*; this is the
 * §4 equivalent, and it is the only way the window's closure becomes a fact on the record
 * rather than an assumption about a device nobody can reach.
 *
 * ## What it deliberately does not carry
 *
 * The `legId` and the reason, and nothing else. Not the address it deleted, obviously; not
 * the Order reference either, because a redaction event is a **device** fact and the leg id
 * is enough to join it to one. An event created to prove PII was destroyed would be a
 * remarkable place to record the PII.
 */
export const deviceLifecycleEventSchema = z.object({
    deviceSequence: z.coerce.number().int().nonnegative(),
    capturedAt: z.string().datetime({ offset: true }),
    kind: z.literal('redaction.completed'),
    legId: z.string().trim().min(1),
    reason: z.enum(REDACTION_REASONS),
});
/**
 * One row from the outbox: a state change, or a device fact.
 *
 * A plain union rather than `z.discriminatedUnion`, because the discriminator would have to
 * be added to `courierEventSchema` — a wire change to the lane that already ships, to
 * describe a distinction the shapes already make. The arms are **disjoint by required
 * fields**: a machine event has `trigger` and `shipmentId` and no `kind`; a lifecycle event
 * has `kind` and `legId` and neither of the others. `courier-lane.test.ts` asserts the
 * disjunction in both directions rather than leaving it to inspection, because "obviously
 * these cannot both parse" is exactly the sentence that ages badly as fields are added.
 */
export const courierOutboxEventSchema = z.union([courierEventSchema, deviceLifecycleEventSchema]);
/** A lifecycle row is the one with a `kind`; narrow with this rather than by field probing. */
export function isLifecycleEvent(event) {
    return 'kind' in event;
}
/**
 * A drained outbox batch.
 *
 * Capped because §8 asks for small payloads over a courier network, and because a batch
 * that fails is retried whole — a large one turns a flaky link into no progress at all.
 *
 * Both lanes ride here together and in **sequence order**, because they share one counter.
 * Splitting them into two requests would have let a redaction be acked while the delivery
 * that preceded it was still queued, which is precisely the ordering the single counter
 * exists to prevent.
 */
export const courierSyncSchema = z.object({
    events: z.array(courierOutboxEventSchema).min(1).max(50),
});
/**
 * Nothing in a batch is refused by the pipe: a 400 refuses the whole batch and stalls the device's
 * queue on one event. `SyncService` checks each event's payload itself and answers a rejected row.
 */
export const courierSyncRendered = renderedText(courierSyncSchema, {
    exempt: {
        'events.*.capturedAt': 'an ISO datetime by zod; stored as a timestamptz and shown re-serialised, verbatim only in an unread payload',
        'events.*.shipmentId': "resolved to one of the Courier's Shipments at ingest; a miss is a rejected row with a code, never the value",
        'events.*.legId': 'stored only in the courier.redaction_completed event payload; no view renders it',
        'events.*.payload': 're-parsed per trigger with applyShipmentSchema in SyncService and checked there against applyShipmentRendered',
    },
});
/**
 * The `policy_config` keys the §4 visibility windows read.
 *
 * Separate from `POLICY_KEYS` in `shipments.ts`, which is the dispatch guards' list. These
 * two are not guard inputs — they are the shape of the window itself, consumed by
 * `legWindow` on the server and, from slice B, by the device's own redaction timer running
 * the identical predicate offline.
 *
 * **Both are unruled numbers** (02a escalated item 8: the TTL "is a policy number, not a
 * modeling question"), so they ship as rows with an owner and a review date rather than
 * constants — `04-milestones.md` Assumption 5. A missing key leaves the policy `undefined`
 * and the working set refuses to answer, which is the point of a row: an unruled number
 * refuses rather than quietly enforcing a default nobody owns.
 */
export const COURIER_POLICY_KEYS = {
    visibilityGraceWindow: 'visibility_grace_window',
    visibilityDeviceTtl: 'visibility_device_ttl',
    /**
     * **N, which the handset needs and never received.**
     *
     * 02a evaluates `n < N` **device-locally** so the re-attempt cycle survives an outage;
     * the machine row's own visibility effect reads "device-local n < N guard"; and
     * `COURIER_TRIGGERS` repeats it above. Yet the working set carried the two window numbers
     * and stopped — the device held `attemptCount` and had nothing to compare it against, so
     * the one guard 02a explicitly delegates to the device could not be evaluated there.
     *
     * Shipped by the same argument as the float ceiling beside it: a limit that cannot be
     * enforced server-side while offline has to travel before the device goes dark. Unlike
     * the two above, this key is a *guard* input as well — `attemptsBelowN` reads it — so it
     * is deliberately the same row, not a courier-only copy that could disagree with the
     * server's own answer.
     */
    deliveryAttemptsMax: 'delivery_attempts_max',
    /**
     * **How many sealed dropoffs one Courier's handset is sent at once** (ADR-0035 §3, P2-1).
     *
     * Residency is bounded twice: the device TTL bounds how long a sealed dropoff stays, and this
     * bounds how many — a handset holding forty Buyers' addresses ahead of their pickups is a
     * different exposure from one holding two, and the TTL alone does not bound breadth. The
     * server applies it, not the device: a handset that received forty and then kept five would
     * already have held forty.
     *
     * Seeded by migration 0011 and **read by nothing until 2026-09-30**, while ADR-0035 said
     * residency was capped by it. Unlike the three keys above, an absent or malformed value does
     * not refuse the working set: it pre-positions **nothing**, the strictest reading, so a
     * Courier keeps working and only loses the offline opening of a dropoff.
     */
    sealedShipmentsMax: 'sealed_shipments_max',
};
// ---------------------------------------------------------------------------
// Down the wire: what the server answers
// ---------------------------------------------------------------------------
/**
 * The response shapes, lifted here in M2 slice B (ADR-0035).
 *
 * They lived in `apps/api` until the device needed them, which was fine while the only
 * reader was the code that wrote them. It stops being fine the moment a second runtime has
 * to *validate* what it stores: two copies of a wire shape drift, and this repository
 * writes tests against exactly that. `courier-contract.test.ts` parses the real HTTP
 * bodies under these schemas, so the description and the thing described cannot separate.
 *
 * Money stays a **string** in minor units all the way to the handset (I2). A device that
 * parsed a COD amount into a `number` would be the float this invariant forbids, arriving
 * by the one route nobody audits.
 */
export const wireMoneySchema = z.object({
    amountMinor: z.string().regex(/^\d+$/u),
    currency: z.string().length(3),
});
/**
 * One Leg as the device receives it.
 *
 * The five top-level keys are `ALLOWED_FIELDS.courier` exactly; the resolver would strip
 * anything else, so a sixth here would describe a response the server cannot emit.
 * `maskedChannelId` is on that allow-list and deliberately never sent — Rooms is M6.
 */
export const workingSetLegSchema = z.object({
    orderReference: z.string(),
    deliveryStatus: z.string(),
    codAmount: wireMoneySchema,
    parcels: z.array(z.object({ barcode: z.string(), scannedAtPickup: z.string().nullable() })),
    legDetails: z.object({
        legId: z.string(),
        /**
         * **Added in slice B, and the device could not work without it.**
         *
         * `courierEventSchema` requires a `shipmentId` and ingest resolves the Shipment by it,
         * but the working set carried only `legId` and `orderReference` — so a handset holding
         * a snapshot had no way to name the thing it was acting on. Slice A's own test read it
         * straight out of Postgres, which is a luxury a device does not have. A platform
         * reference, not party data.
         */
        shipmentId: z.string(),
        kind: z.enum(['pickup', 'delivery', 'transfer', 'return']),
        status: z.string(),
        sequence: z.number().int(),
        barcode: z.string(),
        attemptCount: z.number().int(),
        assignedAt: z.string().nullable(),
        endpointKind: z.enum(['seller', 'buyer', 'hub']),
        endpoint: z
            .object({
            governorate: z.string().nullable(),
            district: z.string().nullable(),
            city: z.string().nullable(),
            landmark: z.string().nullable(),
            building: z.string().nullable(),
            floor: z.string().nullable(),
            // §8: the pin is authoritative and the text advisory. Strings, not numbers — they
            // are cast `::text` server-side precisely so no float rounds a doorstep.
            gpsLat: z.string(),
            gpsLng: z.string(),
            /**
             * Whether that pin is a doorstep or a stand-in (ADR-0055).
             *
             * `provisional` means the Buyer's address arrived as text, so the pin is the Zone's
             * representative point — good enough to plan a route against, and **not** a door. A
             * handset must say so rather than navigate to it confidently, because a
             * confidently-wrong pin is the one thing worse than an address with no pin at all.
             *
             * `.default('verified')` rather than required: a device holding a snapshot taken
             * before this field existed must keep working offline, and every Leg in such a
             * snapshot did carry a caller-supplied pin. The default states that, rather than
             * failing a parse on a handset nobody can reach (I5).
             */
            pinStatus: z.enum(['verified', 'provisional']).default('verified'),
        })
            .nullable(),
        contactAtDoor: z
            .object({
            name: z.string(),
            directions: z.string().nullable(),
            atDoor: z.string().nullable(),
        })
            .nullable(),
        /**
         * ISO UTC this Leg was pre-positioned on a device ahead of its own window (ADR-0035).
         *
         * Present only on a sealed delivery Leg. **Nullable, never optional**, and the
         * difference is the whole safety property: `legDetails` is parsed non-strictly, so an
         * `.optional()` field would be *silently stripped* by a schema that did not declare it —
         * and a device reading absent-as-not-sealed would open a Leg the server sealed. Declared
         * and nullable, its absence is a parse failure rather than a permission.
         */
        sealedAt: z.string().datetime({ offset: true }).nullable().default(null),
        /**
         * Whether this Leg's Order is a rehearsal: its Merchant is a sandbox one (ADR-0077).
         *
         * A sandbox Order posts to its own ledger tree and holds no real money, so a Courier who took
         * real cash at its door would hold custody the books do not know about. The handset says so,
         * on the Leg and where the cash is counted. Not party data: a property of the Merchant's
         * account, and the Courier needs it to act rightly at a door.
         *
         * `.default(false)`, `pinStatus`'s reasoning: a snapshot taken before the field existed holds
         * no rehearsal Orders, so its absence is the truth about it.
         */
        rehearsal: z.boolean().default(false),
    }),
});
/**
 * The whole snapshot — D2's "snapshots down".
 *
 * **Flattened, not nested.** The resolver interceptor merges a `many` projection's meta
 * into the body as `{ items, ...meta }`, so the wire carries no `meta` object however the
 * service spells it internally. Describing it the way the service is written rather than
 * the way it arrives is the near-miss a schema exists to catch.
 */
export const workingSetResponseSchema = z.object({
    items: z.array(workingSetLegSchema),
    syncedAt: z.string().datetime({ offset: true }),
    /** Ships *with* the snapshot so the device runs the identical predicate offline (D2). */
    redactionPolicy: z.object({
        graceWindowMinutes: z.number().int(),
        deviceTtlHours: z.number().int(),
    }),
    /**
     * N, for the one guard 02a delegates to the device.
     *
     * Separate from `redactionPolicy` because they answer different questions — one is the
     * shape of a §4 window, the other is a dispatch threshold — and a device that conflated
     * them would renew the wrong thing on sync.
     */
    attemptPolicy: z.object({ deliveryAttemptsMax: z.number().int() }),
    courier: z
        .object({
        courierId: z.string(),
        firstName: z.string(),
        /**
         * The Courier's English name, or null when none is on file (2026-09-28). The working-set
         * title printed `firstName` — the Arabic column — on an English or French handset.
         * Optional so a device built after this reads a server built before it, and the reverse
         * strips it: neither refuses a working set over a greeting.
         */
        firstNameEn: z.string().nullable().optional(),
        vehicle: z.string(),
        floatCeiling: wireMoneySchema.nullable(),
    })
        .nullable(),
});
/**
 * The acknowledgement — D2's "events up", answered.
 *
 * `ackWatermark` is a **string** because the counter is a `bigint` server-side and JSON has
 * no such thing; `-1` is the honest answer to a device the server has never heard from, so
 * the pattern admits a sign.
 *
 * A `rejected` row arrives inside a **201**: a refusal is data about the batch, not a
 * failure of the request, and everything below it still stands. `error` is an `ErrorCode`
 * and `at` a field path — static vocabulary, never text built from what was sent.
 */
export const syncResultSchema = z.object({
    ackWatermark: z.string().regex(/^-?\d+$/u),
    applied: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
    skewFlagged: z.number().int().nonnegative(),
    rejected: z
        .object({
        deviceSequence: z.string().regex(/^\d+$/u),
        error: z.string(),
        at: z.string().nullable(),
    })
        .optional(),
});
/** What a redeemed enrolment hands back. The token is shown once and never readable again. */
export const enrolledDeviceSchema = z.object({
    deviceId: z.string(),
    courierId: z.string(),
    token: z.string(),
    expiresAt: z.string(),
});
//# sourceMappingURL=courier.js.map